import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { RequireAuth, NoRequireAuth, RequireSuperuser } from "@/guards";
import { ROUTES } from "@/utils/routes";

import AdminLogin from "./pages/auth/login/AdminLogin";
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import Candidates from "./pages/candidates/Candidates";
import CandidateProfileView from "./pages/candidate-profile/CandidateProfileView";
import CandidateProfileEditor from "./pages/candidate-profile/CandidateProfileEditor";
import Teams from "./pages/teams/Teams";
import EmployeeProfile from "./pages/profile/EmployeeProfile";
import MyResumes from "./pages/resumes/MyResumes";
import Templates from "./pages/templates/Templates";

// Master Management Page Components imported directly from their respective files
import DegreeManagement from "./pages/master-management/DegreeManagement";
import IndustryManagement from "./pages/master-management/IndustryManagement";
import TeamManagement from "./pages/master-management/TeamManagement";
import DepartmentManagement from "./pages/master-management/DepartmentManagement";
import CategoryManagement from "./pages/master-management/CategoryManagement";
import SkillManagement from "./pages/master-management/SkillManagement";

import { AppLayout } from "./pages/layout/AppLayout";
import EmployeeLogin from "./pages/auth/login/EmployeeLogin";

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <BrowserRouter>
        <Routes>
          {/* Unauthenticated Login Routes */}
          <Route
            path={ROUTES.LOGIN}
            element={
              <NoRequireAuth>
                <EmployeeLogin />
              </NoRequireAuth>
            }
          />
          <Route
            path={ROUTES.LOGIN_ADMIN}
            element={
              <NoRequireAuth>
                <AdminLogin />
              </NoRequireAuth>
            }
          />

          {/* Protected Main App Routes */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route path="dashboard" element={<AdminDashboard />} />

            {/* Superuser Only Routes */}
            <Route
              path="candidates"
              element={
                <RequireSuperuser>
                  <Candidates />
                </RequireSuperuser>
              }
            />
            <Route
              path="candidates/:id"
              element={
                <RequireSuperuser>
                  <CandidateProfileView />
                </RequireSuperuser>
              }
            />
            <Route
              path="candidates/:id/edit"
              element={
                <RequireSuperuser>
                  <CandidateProfileEditor />
                </RequireSuperuser>
              }
            />
            <Route
              path="teams"
              element={
                <RequireSuperuser>
                  <Teams />
                </RequireSuperuser>
              }
            />

            {/* Direct Master Management Page Routes (Superuser Only) */}
            <Route
              path="degree"
              element={
                <RequireSuperuser>
                  <DegreeManagement />
                </RequireSuperuser>
              }
            />
            <Route
              path="industry"
              element={
                <RequireSuperuser>
                  <IndustryManagement />
                </RequireSuperuser>
              }
            />
            <Route
              path="team"
              element={
                <RequireSuperuser>
                  <TeamManagement />
                </RequireSuperuser>
              }
            />
            <Route
              path="department"
              element={
                <RequireSuperuser>
                  <DepartmentManagement />
                </RequireSuperuser>
              }
            />
            <Route
              path="category"
              element={
                <RequireSuperuser>
                  <CategoryManagement />
                </RequireSuperuser>
              }
            />
            <Route
              path="skill"
              element={
                <RequireSuperuser>
                  <SkillManagement />
                </RequireSuperuser>
              }
            />

            {/* Fallback route */}
            <Route
              path="master-management"
              element={<Navigate to={ROUTES.DEGREE} replace />}
            />

            {/* Standard Protected Routes */}
            <Route path="profile" element={<EmployeeProfile />} />
            <Route path="resumes" element={<MyResumes />} />
            <Route path="templates" element={<Templates />} />

            <Route index element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          </Route>

          <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
